import { FileText, ExternalLink, Copy, Check } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { useState } from 'react';
import { copyToClipboard } from '../lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ApiDocsPage() {
  const [copied, setCopied] = useState(null);

  const handleCopy = async (text, id) => {
    const success = await copyToClipboard(text);
    if (success) {
      setCopied(id);
      setTimeout(() => setCopied(null), 2000);
    }
  };

  const endpoints = [
    {
      method: 'GET',
      path: '/api/',
      description: 'Get chain information',
      response: '{ "message": "GG CHAIN Explorer API", "chain_id": "ggchain-mainnet-1", "version": "1.0.0" }',
    },
    {
      method: 'GET',
      path: '/api/blocks',
      description: 'Get latest blocks',
      params: 'page (int), limit (int, max 100)',
      response: '[{ "height": 1234, "hash": "...", "tx_count": 25, ... }]',
    },
    {
      method: 'GET',
      path: '/api/blocks/{height}',
      description: 'Get block by height or hash',
      response: '{ "height": 1234, "hash": "...", "tx_count": 25, "reward": {...} }',
    },
    {
      method: 'GET',
      path: '/api/blocks/{height}/transactions',
      description: 'Get transactions in a block',
      response: '[{ "hash": "...", "from_address": "...", "value": 100.5 }]',
    },
    {
      method: 'GET',
      path: '/api/transactions',
      description: 'Get latest transactions',
      params: 'page (int), limit (int, max 100)',
      response: '[{ "hash": "...", "block_height": 1234, "value": 50.0 }]',
    },
    {
      method: 'GET',
      path: '/api/transactions/{hash}',
      description: 'Get transaction by hash',
      response: '{ "hash": "...", "from_address": "...", "to_address": "...", "value": 50.0 }',
    },
    {
      method: 'GET',
      path: '/api/address/{address}',
      description: 'Get address information',
      response: '{ "address": "gg...", "balance": 1000.5, "tx_count": 150 }',
    },
    {
      method: 'GET',
      path: '/api/address/{address}/transactions',
      description: 'Get address transactions',
      params: 'page (int), limit (int, max 100)',
      response: '[{ "hash": "...", "value": 10.0, "timestamp": "..." }]',
    },
    {
      method: 'GET',
      path: '/api/stats',
      description: 'Get network statistics',
      response: '{ "current_height": 1234567, "hashrate": 1.5e14, "validators_count": 20 }',
    },
    {
      method: 'GET',
      path: '/api/tokenomics',
      description: 'Get tokenomics information',
      response: '{ "max_supply": 21000000, "epoch_rewards": [...] }',
    },
    {
      method: 'GET',
      path: '/api/mempool',
      description: 'Get pending transactions',
      params: 'limit (int, max 200)',
      response: '[{ "hash": "...", "fee": 0.001, "pending_since": "..." }]',
    },
    {
      method: 'GET',
      path: '/api/validators',
      description: 'Get validator list',
      response: '[{ "address": "gg...", "stake": 50000, "uptime": 0.99 }]',
    },
    {
      method: 'GET',
      path: '/api/miners',
      description: 'Get miner list',
      response: '[{ "address": "gg...", "hashrate": 1e12, "blocks_mined": 500 }]',
    },
    {
      method: 'GET',
      path: '/api/search',
      description: 'Search for block/tx/address',
      params: 'q (string, required)',
      response: '{ "type": "block|transaction|address", "result": "..." }',
    },
  ];

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8" data-testid="api-docs-page">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <div className="p-3 rounded-xl bg-gradient-to-br from-[#00F0FF]/20 to-transparent">
            <FileText className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold">API Documentation</h1>
            <p className="text-sm text-muted-foreground">
              GG CHAIN Explorer REST API Reference
            </p>
          </div>
        </div>

        {/* Base URL */}
        <div className="data-card p-6 mb-8">
          <h2 className="text-lg font-bold mb-4">Base URL</h2>
          <div className="flex items-center gap-3 p-4 rounded-lg bg-white/5">
            <code className="font-mono text-primary flex-1 break-all">
              {BACKEND_URL}
            </code>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleCopy(BACKEND_URL, 'base')}
            >
              {copied === 'base' ? (
                <Check className="w-4 h-4 text-green-400" />
              ) : (
                <Copy className="w-4 h-4" />
              )}
            </Button>
          </div>
        </div>

        {/* Endpoints */}
        <div className="space-y-4">
          {endpoints.map((endpoint, i) => (
            <div key={i} className="data-card overflow-hidden">
              <div className="p-4 border-b border-white/10 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className={`
                    px-2 py-1 rounded text-xs font-mono font-bold
                    ${endpoint.method === 'GET' ? 'bg-green-500/20 text-green-400' : 'bg-blue-500/20 text-blue-400'}
                  `}>
                    {endpoint.method}
                  </span>
                  <code className="font-mono text-sm">{endpoint.path}</code>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-2"
                  onClick={() => handleCopy(`${BACKEND_URL}${endpoint.path}`, `endpoint-${i}`)}
                >
                  {copied === `endpoint-${i}` ? (
                    <Check className="w-4 h-4 text-green-400" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                  Copy
                </Button>
              </div>
              <div className="p-4">
                <p className="text-sm text-muted-foreground mb-3">{endpoint.description}</p>
                
                {endpoint.params && (
                  <div className="mb-3">
                    <p className="text-xs text-muted-foreground mb-1">Parameters:</p>
                    <code className="text-xs font-mono text-yellow-400">{endpoint.params}</code>
                  </div>
                )}
                
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Example Response:</p>
                  <pre className="p-3 rounded-lg bg-black/30 overflow-x-auto text-xs font-mono text-green-400">
                    {endpoint.response}
                  </pre>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Rate Limits */}
        <div className="data-card p-6 mt-8">
          <h2 className="text-lg font-bold mb-4">Rate Limits & Notes</h2>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li>• No authentication required for read-only endpoints</li>
            <li>• Default pagination limit: 20 items</li>
            <li>• Maximum pagination limit: 100 items (mempool: 200)</li>
            <li>• All timestamps are in ISO 8601 format (UTC)</li>
            <li>• Addresses use format: gg + 40 hex characters</li>
          </ul>
        </div>
      </div>
    </Layout>
  );
}
